import { useState } from 'react';
import { attendanceService } from 'services/api';
import { nombreDe, etiquetaUnidad } from './poderes';
import styles from './RepresentativeModal.module.css';

// "Ver poder": datos de un poder ya registrado (desde la fila de una unidad
// cubierta por él) y opción de quitarlo. Si cubre varias unidades se puede
// quitar solo la de la fila desde donde se abrió (unitId) o el poder completo.
// Registrar poderes se hace con "Dar poder a…" (fila de quien lo entrega) o
// "Representa a…" (fila del representante).
export default function RepresentativeModal({ attendance, ownerRows, unitId, onClose, onSaved }) {
  const [saving,        setSaving]        = useState(false);
  const [error,         setError]         = useState('');
  const [confirmQuitar, setConfirmQuitar] = useState(null); // null | 'unidad' | 'todo'

  const BASE_URL  = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:3001';
  const ownerName = attendance.owner ? nombreDe(attendance.owner) : `Unidad ${etiquetaUnidad(attendance) || attendance.id}`;
  const unidades  = (attendance.rep_unidades || [attendance.unit_id]).map(Number);
  const numeroDe  = (id) => etiquetaUnidad((ownerRows || []).find(r => Number(r.unit_id) === id)) || id;
  const varias    = unidades.length > 1 && unidades.includes(Number(unitId));
  const unidadFila = numeroDe(Number(unitId));
  const otras      = unidades.filter(u => u !== Number(unitId)).map(numeroDe).join(', ');

  const quitarPoder = async () => {
    setError(''); setSaving(true);
    try {
      if (confirmQuitar === 'unidad') {
        await attendanceService.removeRepresentative(attendance.id, [Number(unitId)]);
        onSaved(`Unidad ${unidadFila} quitada del poder`);
      } else {
        await attendanceService.removeRepresentative(attendance.id);
        onSaved('Poder quitado');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'No se pudo quitar el poder');
      setConfirmQuitar(null);
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className={`modal ${styles.caja}`} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h3>
              <i className={`fas fa-file-signature ${styles.iconoTitulo}`} />
              Poder registrado
            </h3>
            <div className={styles.subtitulo}>
              <strong>{attendance.rep_nombre}</strong> vota en nombre de <strong>{ownerName}</strong>
            </div>
          </div>
          <button className="btn-close" onClick={onClose}>✕</button>
        </div>

        <div className={`modal-body ${styles.cuerpo}`}>
          {error && <div className={`alert alert-danger ${styles.avisoSinMargen}`}>{error}</div>}
          <div className={styles.datos}>
            <span className={styles.dato}>Entrega el poder</span><strong>{ownerName}</strong>
            <span className={styles.dato}>Representante</span><strong>{attendance.rep_nombre}</strong>
            <span className={styles.dato}>Documento</span><span>{attendance.rep_numero_documento}</span>
            <span className={styles.dato}>Tipo</span>
            <span>{attendance.rep_es_externo ? 'Persona externa' : 'Propietario del conjunto'}</span>
            <span className={styles.dato}>Unidades</span>
            <span>{unidades.map(numeroDe).join(', ')}</span>
            <span className={styles.dato}>Documento de poder</span>
            <span>
              {attendance.rep_path_documento ? (
                <a href={`${BASE_URL}${attendance.rep_path_documento}`} target="_blank" rel="noreferrer">
                  <i className="fas fa-file-alt" /> Ver documento
                </a>
              ) : '—'}
            </span>
          </div>
          {confirmQuitar === 'unidad' && (
            <div className={`alert alert-warning ${styles.avisoSinMargen}`}>
              ¿Quitar solo la unidad {unidadFila}? Vuelve a {ownerName} y {attendance.rep_nombre} sigue
              representando la{otras.includes(',') ? 's unidades' : ' unidad'} {otras} con el mismo documento.
              No se puede si ya se votó con esa unidad.
            </div>
          )}
          {confirmQuitar === 'todo' && (
            <div className={`alert alert-warning ${styles.avisoSinMargen}`}>
              ¿Quitar el poder completo? {unidades.length > 1 ? `Las unidades ${unidades.map(numeroDe).join(', ')}` : 'La unidad'} vuelve{unidades.length > 1 ? 'n' : ''} a{' '}
              {ownerName} y {attendance.rep_nombre} ya no podrá votar por {unidades.length > 1 ? 'ellas' : 'ella'}.
              No se puede si ya se votó con este poder.
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cerrar</button>
          {confirmQuitar ? (
            <button type="button" className="btn btn-danger" onClick={quitarPoder} disabled={saving}>
              {saving ? <><i className="fas fa-spinner fa-spin" /> Quitando...</>
                : <><i className="fas fa-user-minus" /> {confirmQuitar === 'unidad' ? `Sí, quitar la ${unidadFila}` : 'Sí, quitar poder'}</>}
            </button>
          ) : (
            <>
              {varias && (
                <button type="button" className={`btn btn-secondary ${styles.botonQuitarUnidad}`} onClick={() => setConfirmQuitar('unidad')}>
                  <i className="fas fa-minus-circle" /> Quitar solo la unidad {unidadFila}
                </button>
              )}
              <button type="button" className="btn btn-danger" onClick={() => setConfirmQuitar('todo')}>
                <i className="fas fa-user-minus" /> {varias ? 'Quitar todo el poder' : 'Quitar poder'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
